// ─── Backend dates → user timezone ──────────────────────────────────────────
// The backend persists UTC wall-clock and serializes it WITHOUT an offset
// (e.g. "2026-09-13T03:04:14.807773"). Plain `new Date(str)` reads such
// strings as browser-local time, shifting every timestamp by the UTC offset.
// These helpers interpret offset-less backend strings as UTC and format them
// in the user's (browser) timezone. Date-only values ("YYYY-MM-DD") are
// calendar dates — constructed locally with no shift.

export function parseBackendDate(input?: string | null): Date | null {
  if (input == null) return null;
  let s = String(input).trim();
  if (!s) return null;
  // calendar date only → construct in the local zone (no shift)
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dateOnly) {
    const d = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  // some payloads use a space separator → normalize to ISO first
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = s.replace(' ', 'T');
  // explicit offset / Z → parse as-is; otherwise the value is UTC
  const d = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? new Date(s) : new Date(`${s}Z`);
  return isNaN(d.getTime()) ? null : d;
}

export function formatUserDateTime(input?: string | null, opts?: Intl.DateTimeFormatOptions): string {
  const d = parseBackendDate(input);
  if (!d) return '';
  return d.toLocaleString(undefined, opts ?? { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatUserDate(input?: string | null, opts?: Intl.DateTimeFormatOptions): string {
  const d = parseBackendDate(input);
  if (!d) return '';
  return d.toLocaleDateString(undefined, opts ?? { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatUserTime(input?: string | null, opts?: Intl.DateTimeFormatOptions): string {
  const d = parseBackendDate(input);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, opts ?? { hour: '2-digit', minute: '2-digit' });
}

export function formatLocalDate(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// End of the calendar day (local) for a date-only input — upper bound for filters.
export function endOfLocalDay(input?: string | null): number | null {
  const d = parseBackendDate(input);
  if (!d) return null;
  return d.getTime() + 86400000 - 1;
}
